import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { MakeBridgeContext } from '@atlassian/forge-ui/bridge-types';
import type { IframeProps } from '@atlassian/forge-ui/custom-ui/iframe/types';

import type { EditorAPI } from '../../types';

type ContentData = {
	data: string;
};

interface GetCustomBridgeMethodsV2Params {
	editorAPI?: EditorAPI;
}

type EditorContext = MakeBridgeContext<'resolverContext' | 'extensionPayload'>;

/**
 * v2 version of `getCustomBridgeMethods`.
 *
 * Returns a `customBridge`-compatible map of bridge methods
 * (i.e. `(context, payload) => Promise<Result>`) for use with `createBridgeFeatures`
 * via the `consolidateCustomBridge` utility.
 *
 * Unlike the v1 version (which returns plain `(data) => Promise` callbacks that get
 * adapted via `adaptLegacyBridgeMethod`), v2 methods receive the full `BridgeContext`
 * as their first argument — enabling access to `localId`, `isEditing`, etc.
 * at call time rather than at creation time.
 */
export const getCustomBridgeMethodsV2 = ({
	editorAPI,
}: GetCustomBridgeMethodsV2Params): NonNullable<IframeProps['customBridge']> => {
	const getEditorContent = async (
		{ extensionPayload }: EditorContext,
		_payload: unknown,
	): Promise<ContentData | undefined> => {
		if (!extensionPayload?.isEditing) {
			return undefined;
		}
		return new Promise<ContentData>((resolve) => {
			editorAPI?.core?.actions.requestDocument((doc) => {
				if (!doc) {
					resolve({ data: '' });
				} else {
					resolve({ data: JSON.stringify(doc) });
				}
			});
		});
	};

	const getMacroContent = async (
		{ resolverContext, extensionPayload }: EditorContext,
		_payload: unknown,
	): Promise<ContentData | undefined> => {
		if (!extensionPayload?.isEditing) {
			return undefined;
		}
		const extensionAPI = editorAPI?.extension?.actions?.api();
		if (!extensionAPI) {
			throw new Error('Unable to get macro content: extensionAPI is not available');
		}

		try {
			const macroLocalId = resolverContext?.localId;
			const nodeWithPos = extensionAPI.getNodeWithPosByLocalId(macroLocalId);
			const { node } = nodeWithPos;

			return { data: JSON.stringify(node) };
		} catch (error) {
			throw new Error(`Failed to get macro: ${error}`);
		}
	};

	const updateMacro = async (
		{ resolverContext, extensionPayload }: EditorContext,
		payload: unknown,
	): Promise<boolean> => {
		if (!extensionPayload?.isEditing) {
			return false;
		}
		const newExtensionContent = payload as ContentData | undefined;
		if (!newExtensionContent?.data) {
			return false;
		}

		try {
			const macroLocalId = resolverContext?.localId;
			const newExtensionData = JSON.parse(newExtensionContent.data);
			const extensionAPI = editorAPI?.extension?.actions?.api();

			if (extensionAPI && newExtensionData) {
				extensionAPI.doc?.update(macroLocalId, () => newExtensionData);
				return true;
			}
		} catch (error) {
			throw new Error(`Failed to update macro: ${error}`);
		}
		return false;
	};

	const setMacroViewportHeight = async (
		{ resolverContext, extensionPayload }: EditorContext,
		payload: unknown,
	): Promise<boolean> => {
		if (!extensionPayload?.isEditing) {
			return false;
		}
		if (!fg('platform_measure_macro_height')) {
			throw new Error('setMacroViewportHeight is not available');
		}

		const height = payload as string;
		const macroLocalId = resolverContext?.localId;
		try {
			const extensionAPI = editorAPI?.extension?.actions?.api();

			if (extensionAPI) {
				extensionAPI.doc?.update(macroLocalId, (currNode) => ({
					attrs: {
						...currNode.attrs,
						parameters: {
							...currNode.attrs?.parameters,
							viewportHeight: height,
						},
					},
				}));

				return true;
			}
		} catch (error) {
			throw new Error(`Failed to set the macro's viewport height: ${error}`);
		}

		return false;
	};

	return {
		getEditorContent,
		getMacroContent,
		updateMacro,
		setMacroViewportHeight,
	};
};
